// frontend/src/components/event/EventRegistrationForm.js
//
// Registration form for events. Handles 5 scenarios based on format + external_registration_url:
//
// 1. online                             → Internal registration (online only)
// 2. in_person + external_registration_url  → Link to external organizer (no internal registration)
// 3. in_person (no external URL)            → Internal registration (in-person only)
// 4. hybrid + external_registration_url     → Online = internal register, Vor Ort = external link
// 5. hybrid (no external URL)               → Two-option selector (online / in-person), both internal
//
// Waitlist: When a participation_type is full but waitlist_enabled, shows an amber
// "Join Waiting List" button instead of "No spots available".
// Each participation_type is evaluated independently (hybrid can have one open, one waitlisted).
//
import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Monitor, MapPin, Loader2, ExternalLink, Clock } from "lucide-react";

const EventRegistrationForm = ({
  event,
  onRegister,
  loading = false,
  isUrgent = false,
}) => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [participationType, setParticipationType] = useState(
    event.format === "in_person" ? "in_person" : "online"
  );
  const [emailConsentPartner, setEmailConsentPartner] = useState(false);

  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";

  const hasExternalRegistration = !!event.external_registration_url;
  const isWaitlistEnabled = event.capacity?.waitlist_enabled !== false && event.capacity?.waitlist_enabled !== 0;

  const buttonLabel = isInvited
    ? t({ de: "Einladung annehmen & Zugang erhalten", en: "Accept Invitation & Get Access" })
    : isUrgent
    ? t({ de: "Anmelden & Beitreten", en: "Register & Join" })
    : t({ de: "Jetzt anmelden", en: "Register Now" });

  const waitlistLabel = t({ de: "Auf Warteliste setzen", en: "Join Waiting List" });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${event.slug}`);
      return;
    }

    onRegister(participationType, event.partner_consent_text ? emailConsentPartner : null);
  };

  // Check available spots (null = unlimited = always available)
  const onlineSpotsAvailable =
    event.capacity?.spotsRemaining?.online === null ||
    event.capacity?.spotsRemaining?.online > 0;
  const inPersonSpotsAvailable =
    event.capacity?.spotsRemaining?.in_person === null ||
    event.capacity?.spotsRemaining?.in_person > 0;

  // Determine if waitlist applies per type (full + waitlist enabled)
  const onlineCanWaitlist = !onlineSpotsAvailable && isWaitlistEnabled;
  const inPersonCanWaitlist = !inPersonSpotsAvailable && isWaitlistEnabled;

  // Register or waitlist button (used in multiple scenarios)
  const RegisterButton = ({ type = "submit", disabled, isWaitlist, className, onClick }) => (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`${className || "w-full"} ${
        isWaitlist
          ? "bg-amber-500 text-white hover:bg-amber-600"
          : "bg-[#EE005F] text-white hover:bg-[#cc0050]"
      } py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
    >
      {loading && <Loader2 className="w-5 h-5 animate-spin" />}
      {isWaitlist ? (
        <>
          <Clock className="w-5 h-5" />
          {waitlistLabel}
        </>
      ) : (
        buttonLabel
      )}
    </button>
  );

  // No spots and no waitlist message
  const NoSpotsMessage = () => (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
      {t({ de: "Keine Plätze mehr verfügbar", en: "No spots available" })}
    </div>
  );

  // Partner email consent checkbox (shown only when event has partner_consent_text)
  const PartnerConsentCheckbox = () => {
    if (!event.partner_consent_text) return null;
    return (
      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
        <input
          type="checkbox"
          checked={emailConsentPartner}
          onChange={(e) => setEmailConsentPartner(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-gray-300 text-[#EE005F] focus:ring-[#EE005F]"
        />
        <span className="text-sm text-gray-700">{event.partner_consent_text}</span>
      </label>
    );
  };

  // For online-only events
  if (event.format === "online") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!onlineSpotsAvailable && !onlineCanWaitlist ? (
          <NoSpotsMessage />
        ) : (
          <>
            <PartnerConsentCheckbox />
            <RegisterButton isWaitlist={onlineCanWaitlist} />
          </>
        )}
      </form>
    );
  }

  const inPersonNotice = (event.format === "in_person") && (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
      <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-800">
        <span className="font-semibold">
          {t({ de: "Vor-Ort-Veranstaltung", en: "In-Person Event" })}
        </span>
        {" – "}
        {t({
          de: "Dieses Event findet physisch vor Ort statt. Es handelt sich nicht um ein Online-Event.",
          en: "This event takes place physically on-site. It is not an online event.",
        })}
        {event.location && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 flex items-center gap-1 font-medium text-amber-800 underline hover:text-amber-900"
          >
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            {event.location}
          </a>
        )}
      </div>
    </div>
  );

  // For in-person only events with external registration
  if (event.format === "in_person" && hasExternalRegistration) {
    return (
      <div className="space-y-4">
        {inPersonNotice}
        <p className="text-sm text-gray-600">
          {t({
            de: "Die Anmeldung für dieses Event wird vom Veranstalter verwaltet.",
            en: "Registration for this event is managed by the organizer.",
          })}
        </p>
        <a
          href={event.external_registration_url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors flex items-center justify-center gap-2"
        >
          {t({ de: "Beim Veranstalter anmelden", en: "Register with Organizer" })}
          <ExternalLink className="w-5 h-5" />
        </a>
      </div>
    );
  }

  // For in-person only events (no external registration)
  if (event.format === "in_person") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {inPersonNotice}
        {!inPersonSpotsAvailable && !inPersonCanWaitlist ? (
          <NoSpotsMessage />
        ) : (
          <>
            <input type="hidden" value="in_person" />
            <PartnerConsentCheckbox />
            <RegisterButton isWaitlist={inPersonCanWaitlist} />
          </>
        )}
      </form>
    );
  }

  // For hybrid events with external registration for in-person
  if (hasExternalRegistration) {
    return (
      <div className="space-y-4">
        <div className="text-sm font-medium text-gray-700 mb-2">
          {t({ de: "Wie möchtest du teilnehmen?", en: "How would you like to participate?" })}
        </div>

        <PartnerConsentCheckbox />

        <div className="space-y-3">
          {/* Online Option - internal registration */}
          <div
            className={`p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              !onlineSpotsAvailable && !onlineCanWaitlist ? "opacity-50" : "border-gray-200"
            }`}
          >
            <Monitor className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {t({ de: "Online", en: "Online" })}
              </div>
              {event.capacity?.spotsRemaining?.online !== null && (
                <div className="text-xs text-gray-500">
                  {onlineSpotsAvailable
                    ? `${event.capacity.spotsRemaining.online} ${t({ de: "Plätze", en: "spots" })}`
                    : onlineCanWaitlist
                    ? t({ de: "Ausgebucht – Warteliste möglich", en: "Fully booked – waitlist available" })
                    : t({ de: "Ausgebucht", en: "Fully booked" })}
                </div>
              )}
            </div>
            {onlineSpotsAvailable ? (
              <form onSubmit={(e) => { e.preventDefault(); if (!isAuthenticated) { navigate(`/login?returnUrl=/event/${event.slug}`); return; } onRegister("online", event.partner_consent_text ? emailConsentPartner : null); }} className="flex-shrink-0">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#EE005F] text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t({ de: "Anmelden", en: "Register" })}
                </button>
              </form>
            ) : onlineCanWaitlist ? (
              <form onSubmit={(e) => { e.preventDefault(); if (!isAuthenticated) { navigate(`/login?returnUrl=/event/${event.slug}`); return; } onRegister("online", event.partner_consent_text ? emailConsentPartner : null); }} className="flex-shrink-0">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-amber-500 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Clock className="w-4 h-4" />
                  {t({ de: "Warteliste", en: "Waitlist" })}
                </button>
              </form>
            ) : (
              <div className="text-xs text-yellow-700 flex-shrink-0">
                {t({ de: "Ausgebucht", en: "Fully booked" })}
              </div>
            )}
          </div>

          {/* In-Person Option - external registration */}
          <div className="p-3 rounded-lg border-2 border-gray-200 flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {t({ de: "Vor Ort", en: "In-Person" })}{event.location ? ` (${event.location})` : ""}
              </div>
              <div className="text-xs text-gray-500">
                {t({ de: "Extern", en: "External" })}
              </div>
            </div>
            <a
              href={event.external_registration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EE005F] text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-[#cc0050] transition-colors flex items-center gap-1 flex-shrink-0"
            >
              {t({ de: "Anmelden", en: "Register" })}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // For hybrid events without external registration - show both options
  // Each participation type independently shows register or waitlist
  const onlineIsWaitlist = onlineCanWaitlist;
  const inPersonIsWaitlist = inPersonCanWaitlist;
  const onlineDisabled = !onlineSpotsAvailable && !onlineCanWaitlist;
  const inPersonDisabled = !inPersonSpotsAvailable && !inPersonCanWaitlist;

  // Determine button label based on selected type
  const selectedIsWaitlist =
    (participationType === "online" && onlineIsWaitlist) ||
    (participationType === "in_person" && inPersonIsWaitlist);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-sm font-medium text-gray-700 mb-2">
        {t({ de: "Wie möchtest du teilnehmen?", en: "How would you like to participate?" })}
      </div>

      <div className="space-y-3">
        {/* Online Option */}
        <button
          type="button"
          onClick={() => setParticipationType("online")}
          disabled={onlineDisabled}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 text-left ${
            participationType === "online"
              ? onlineIsWaitlist
                ? "border-amber-500 bg-amber-500/5"
                : "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${onlineDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Monitor
            className={`w-5 h-5 flex-shrink-0 ${
              participationType === "online"
                ? onlineIsWaitlist ? "text-amber-500" : "text-[#EE005F]"
                : "text-gray-500"
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">
              {t({ de: "Online", en: "Online" })}
            </div>
            {event.capacity?.spotsRemaining?.online !== null && (
              <div className="text-xs text-gray-500">
                {onlineSpotsAvailable
                  ? `${event.capacity.spotsRemaining.online} ${t({ de: "Plätze", en: "spots" })}`
                  : onlineCanWaitlist
                  ? t({ de: "Ausgebucht – Warteliste", en: "Full – waitlist" })
                  : t({ de: "Ausgebucht", en: "Fully booked" })}
              </div>
            )}
          </div>
          {onlineIsWaitlist && participationType === "online" && (
            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
          )}
        </button>

        {/* In-Person Option */}
        <button
          type="button"
          onClick={() => setParticipationType("in_person")}
          disabled={inPersonDisabled}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 text-left ${
            participationType === "in_person"
              ? inPersonIsWaitlist
                ? "border-amber-500 bg-amber-500/5"
                : "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${inPersonDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <MapPin
            className={`w-5 h-5 flex-shrink-0 ${
              participationType === "in_person"
                ? inPersonIsWaitlist ? "text-amber-500" : "text-[#EE005F]"
                : "text-gray-500"
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">
              {t({ de: "Vor Ort", en: "In-Person" })}{event.location ? ` (${event.location})` : ""}
            </div>
            {event.capacity?.spotsRemaining?.in_person !== null && (
              <div className="text-xs text-gray-500">
                {inPersonSpotsAvailable
                  ? `${event.capacity.spotsRemaining.in_person} ${t({ de: "Plätze", en: "spots" })}`
                  : inPersonCanWaitlist
                  ? t({ de: "Ausgebucht – Warteliste", en: "Full – waitlist" })
                  : t({ de: "Ausgebucht", en: "Fully booked" })}
              </div>
            )}
          </div>
          {inPersonIsWaitlist && participationType === "in_person" && (
            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
          )}
        </button>
      </div>

      <PartnerConsentCheckbox />

      <button
        type="submit"
        disabled={
          loading ||
          (participationType === "online" && onlineDisabled) ||
          (participationType === "in_person" && inPersonDisabled)
        }
        className={`w-full ${
          selectedIsWaitlist
            ? "bg-amber-500 hover:bg-amber-600"
            : "bg-[#EE005F] hover:bg-[#cc0050]"
        } text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
      >
        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
        {selectedIsWaitlist ? (
          <>
            <Clock className="w-5 h-5" />
            {waitlistLabel}
          </>
        ) : (
          buttonLabel
        )}
      </button>
    </form>
  );
};

export default EventRegistrationForm;
